import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { useMediaQuery } from 'react-responsive';
import { styled, useTheme } from 'styled-components';
import { useClickOutside } from '@/js/libs/use-click-outside/index.tsx';
import BarsLoader from '../../Elements/BarsLoader/index.js';
import Icons from '../../Elements/Icons/index.js';
import { Media, Sizes } from '../../theme/Media.js';
import { useTableContext } from './context.js';
const Container = styled.div `
  position: absolute;
  top: 0;
  bottom: 0;
  will-change: left;
  @media ${({ $isCollapsedNav }) => ($isCollapsedNav ? Media.lgMin : Media.xlgMin)} {
    right: 100%;
    left: auto;
  }
  @media ${({ $isCollapsedNav }) => ($isCollapsedNav ? Media.mdMax : Media.lgMax)} {
    right: 0;
    top: 100%;
  }
`;
const InnerContainer = styled.div `
  width: auto;
  height: 100%;
  display: flex;
  @media ${({ $isCollapsedNav }) => ($isCollapsedNav ? Media.lgMin : Media.xlgMin)} {
    flex-direction: row;
    & > *:not(:first-child) {
      position: relative;
      &:before {
        content: '';
        display: block;
        position: absolute;
        left: -1px;
        top: 0;
        bottom: 0;
        margin: auto;
        width: 2px;
        height: 22px;
        background: ${({ theme }) => theme.colors.grey10_1};
      }
    }
  }
  @media ${({ $isCollapsedNav }) => ($isCollapsedNav ? Media.mdMax : Media.lgMax)} {
    flex-direction: column-reverse;
    height: auto;
  }
`;
const Wrapper = styled.div `
  position: relative;
  height: 64px;
  width: 64px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: ${({ $isLoading, disabled }) => ($isLoading || disabled ? 'default' : 'pointer')};
  pointer-events: ${({ disabled }) => disabled && 'none'};
  opacity: ${({ disabled }) => disabled && 0.7};
  background: ${({ $active, theme }) => $active && theme.colors.grey00_1};
  transition: background 0.3s;
  @media ${({ $isCollapsedNav }) => ($isCollapsedNav ? Media.mdMax : Media.lgMax)} {
    background: ${({ $active, theme }) => $active && theme.colors.black_1};
    width: auto;
  }
`;
const ActionsList = ({ children, rowIndex = undefined, isLoading = false, disabled = false, dataTestId, hiddenEmptyList = false, }) => {
    const theme = useTheme();
    const [refMenuWrapper, hasClickedOutsideMenuWrapper] = useClickOutside();
    const [standaloneActive, setStandaloneActive] = React.useState(false);
    const { activeRow, setActiveRow, isCollapsedNav } = useTableContext();
    const isSmallScreen = useMediaQuery({
        maxWidth: isCollapsedNav ? Sizes.mdMax : Sizes.lgMax,
    });
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-restricted-syntax
    const toggleActions = React.useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
        if (rowIndex !== undefined) {
            setActiveRow((currentActiveRow) => (currentActiveRow === rowIndex ? undefined : rowIndex));
        }
        if (rowIndex === undefined) {
            setStandaloneActive(!standaloneActive);
        }
    }, [setActiveRow, rowIndex, standaloneActive, setStandaloneActive]);
    React.useEffect(() => {
        if (hasClickedOutsideMenuWrapper) {
            if (rowIndex !== undefined) {
                setActiveRow(undefined);
            }
            if (rowIndex === undefined) {
                setStandaloneActive(false);
            }
        }
    }, [hasClickedOutsideMenuWrapper, setActiveRow, rowIndex, setStandaloneActive]);
    const hasNoChildren = React.useMemo(() => !React.Children.map(children, (c) => c)?.some((c) => !!c), [children]);
    const wrapperDisabled = React.useMemo(() => {
        if (hasNoChildren) {
            return true;
        }
        if (isLoading) {
            return false;
        }
        return disabled;
    }, [disabled, isLoading, hasNoChildren]);
    if (hiddenEmptyList && hasNoChildren) {
        return null;
    }
    return (_jsxs(Wrapper, { onClick: toggleActions, ref: refMenuWrapper, $isLoading: isLoading, disabled: wrapperDisabled, $active: rowIndex === undefined ? standaloneActive : activeRow === rowIndex, "data-testid": dataTestId, $isCollapsedNav: isCollapsedNav, children: [isLoading ? (_jsx(BarsLoader, { bgColor: theme.colors.grey70_1 })) : (_jsx(_Fragment, { children: (rowIndex === undefined ? standaloneActive : activeRow === rowIndex) ? (_jsx(Icons.Cancel, { color: isSmallScreen ? theme.colors.grey00_1 : theme.colors.black_1 })) : (_jsx(Icons.Ellipsis, { direction: isSmallScreen ? 'horizontal' : 'vertical' })) })), (rowIndex === undefined ? standaloneActive : activeRow === rowIndex) && (_jsx(Container, { $isCollapsedNav: isCollapsedNav, children: _jsx(InnerContainer, { $isCollapsedNav: isCollapsedNav, children: children }) }))] }, rowIndex));
};
export default React.memo(ActionsList);
